import React, {useMemo} from "react";
import {Button, Card, Space, Tag, Typography} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {generatePath, matchPath, useLocation, useNavigate} from "react-router-dom";
import useCurrentUserRoles from "../../hooks/useCurrentUserRoles";
import {
    getNavigationItemByRoute,
    getRelatedNavigationItems,
    getSectionDefinition,
    getVisibleActions,
    routeContexts
} from "../../navigation/navigationConfig";
import {RoutingPaths} from "../../pages/routing/routingContainer";
import styles from "./routeContextBanner.module.less";

export default function RouteContextBanner() {
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const roles = useCurrentUserRoles();
    const resolvedRoles = roles ?? undefined;

    const context = useMemo(() => {
        return routeContexts.find(item =>
            !!matchPath({path: item.routePattern, end: true}, pathname)
        );
    }, [pathname]);

    if (!context) {
        return null;
    }

    const section = getSectionDefinition(context.section);
    const visibleActions = getVisibleActions(context, resolvedRoles);
    const relatedItems = visibleActions.length > 0
        ? []
        : getRelatedNavigationItems(context.section, resolvedRoles, context.navRoute);
    const parentNavigationItem = getNavigationItemByRoute(context.navRoute, resolvedRoles);
    const isTopLevelView = !context.routePattern.includes(":") && context.navRoute === context.routePattern;
    let backRoute = context.navRoute;
    if (context.routePattern === RoutingPaths.warehouseStockTable.route) {
        const match = matchPath({path: RoutingPaths.warehouseStockTable.route, end: true}, pathname);
        const currentWarehouseId = match?.params?.warehouseId;

        if (currentWarehouseId) {
            backRoute = generatePath(RoutingPaths.subWarehouseTable.route, {
                parentWarehouseId: currentWarehouseId
            });
        }
    }

    if (!isTopLevelView) {
        return (
            <div className={styles.wrapper}>
                <Card
                    bordered={false}
                    className={styles.compactBanner}
                    style={{"--section-color": section.color} as React.CSSProperties}
                >
                    <div className={styles.compactRow}>
                        <div className={styles.compactCopy}>
                            <Space size={[8, 8]} wrap>
                                <Tag color={section.color} className={styles.sectionTag}>
                                    {section.label}
                                </Tag>
                                {parentNavigationItem && (
                                    <>
                                        <Typography.Text className={styles.parentLabel}>
                                            {parentNavigationItem.displayName}
                                        </Typography.Text>
                                        <RightOutlined className={styles.breadcrumbIcon}/>
                                    </>
                                )}
                                <Typography.Text className={styles.pageTitle}>
                                    {context.title}
                                </Typography.Text>
                            </Space>
                        </div>
                        <Space wrap>
                            {backRoute && parentNavigationItem && (
                                <Button icon={<LeftOutlined/>} onClick={() => navigate(backRoute)}>
                                    Powrót do {parentNavigationItem.displayName}
                                </Button>
                            )}
                        </Space>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <Card
                bordered={false}
                className={styles.banner}
                style={{"--section-color": section.color} as React.CSSProperties}
            >
                <div className={styles.headerRow}>
                    <div className={styles.copy}>
                        <Space size={[8, 8]} wrap>
                            <Tag color={section.color} className={styles.sectionTag}>
                                {section.label}
                            </Tag>
                            <Typography.Text className={styles.pageTitle}>
                                {context.title}
                            </Typography.Text>
                        </Space>
                    </div>
                    {(visibleActions.length > 0 || relatedItems.length > 0) && (
                        <div className={styles.actions}>
                            <Space wrap>
                                {visibleActions.map(action => (
                                    <Button key={action.route} onClick={() => navigate(action.route)}>
                                        {action.label}
                                    </Button>
                                ))}
                                {relatedItems.map(item => (
                                    <Button key={item.route} onClick={() => navigate(item.route)}>
                                        {item.displayName}
                                    </Button>
                                ))}
                            </Space>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
}
